import {
  createRoute,
  lazyRouteComponent,
  stripSearchParams,
  useLocation,
} from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import { guardRoute } from '../auth/guard.ts'
import { MATCH_TABS, type MatchTab } from '../data/match-tabs.ts'
import { numericIdParam, parseOneOf, parsePositiveInt } from './params.ts'
import { rootRoute } from './root.tsx'

export interface MatchSearch {
  readonly tab: MatchTab
}

export function parseMatchTab(value: unknown): MatchTab {
  return parseOneOf(MATCH_TABS, value) ?? DEFAULT_MATCH_SEARCH.tab
}

/** The tab the page opens on, which therefore does not belong in the URL. */
const DEFAULT_MATCH_SEARCH: MatchSearch = { tab: 'highlights' }

export interface MatchParams {
  readonly matchId: number
}

const matchIdParam = numericIdParam('matchId')

// A session and no role on two of the three: «public» is every authenticated reader, and
// what an outsider sees less of the page decides. Guard map: `guards.test.ts`.
export const matchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/matches/$matchId',
  params: matchIdParam,
  validateSearch: (search: Record<string, unknown>): MatchSearch => ({
    tab: parseMatchTab(search.tab),
  }),
  search: {
    middlewares: [stripSearchParams(DEFAULT_MATCH_SEARCH)],
  },
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  // The page opens on its header, which the shell floats its controls over.
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/match.tsx')),
})

export function matchDetailLink(matchId: number) {
  return {
    to: '/matches/$matchId',
    params: { matchId },
    search: DEFAULT_MATCH_SEARCH,
  } as const
}

/** How the convocazione has just gone out, for the match page the flow lands on. */
export type ConvocazioneLanding = 'published' | 'updated'

declare module '@tanstack/history' {
  interface HistoryState {
    /** Put on the match's entry by the call-up flow, after «Pubblica» or «Aggiorna». */
    landing?: ConvocazioneLanding
  }
}

/** The match page after «Pubblica» or «Aggiorna», in place of the flow: «indietro» does not reopen it. */
export function matchLandingLink(matchId: number, landing: ConvocazioneLanding) {
  return { ...matchDetailLink(matchId), state: { landing }, replace: true } as const
}

/** The landing the flow left on this entry, read once and taken off it: the entry is replaced without it,
 *  so that «indietro» and a reload find none, and the band is seen once. */
export function useLandingOnce(): ConvocazioneLanding | undefined {
  const navigate = matchRoute.useNavigate()
  const onEntry = useLocation({ select: (location) => location.state.landing })
  const [landing] = useState(onEntry)
  useEffect(() => {
    if (onEntry === undefined) return
    void navigate({ search: true, replace: true })
  }, [onEntry, navigate])
  return landing
}

export const lineupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/matches/$matchId/lineup',
  params: matchIdParam,
  // The flow has its own bar and its own way out: the navigation would sit under it. The app's
  // header stays.
  staticData: { chrome: 'flow' },
  beforeLoad: ({ context, location }) =>
    guardRoute({
      queryClient: context.queryClient,
      requiredRoles: ['club', 'coach'],
      currentPath: location.href,
    }),
  component: lazyRouteComponent(() => import('../pages/lineup.tsx')),
})

export function lineupLink(matchId: number) {
  return { to: '/matches/$matchId/lineup', params: { matchId } } as const
}

interface HighlightParams extends MatchParams {
  readonly highlightId: number
}

/** Two ids, so the factory does not fit: both must parse or the route does not match. */
const highlightIdParam = {
  parse: (raw: Record<string, string>): HighlightParams | false => {
    const matchId = parsePositiveInt(raw.matchId)
    const highlightId = parsePositiveInt(raw.highlightId)
    if (matchId === null || highlightId === null) return false
    return { matchId, highlightId }
  },
  stringify: ({ matchId, highlightId }: HighlightParams) => ({
    matchId: String(matchId),
    highlightId: String(highlightId),
  }),
}

export const highlightRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/matches/$matchId/highlights/$highlightId',
  params: highlightIdParam,
  beforeLoad: ({ context, location }) =>
    guardRoute({ queryClient: context.queryClient, currentPath: location.href }),
  staticData: { opensOnHero: true },
  component: lazyRouteComponent(() => import('../pages/highlight.tsx')),
})

export function highlightLink(matchId: number, highlightId: number) {
  return {
    to: '/matches/$matchId/highlights/$highlightId',
    params: { matchId, highlightId },
  } as const
}
